/**
 * "Où nous trouver" — Google-Maps overview for the home & about pages.
 *
 * Uses the legacy `maps.google.com/maps?...&output=embed` URL so no API key
 * is required. The iframe is `loading="lazy"` and capped at a 16/9 ratio.
 *
 * The address is configurable per usage; defaults to the venue used in seed
 * data ("Boulevard Zerktouni, Casablanca").
 */

type Props = {
  /** Full street address shown to visitors. */
  address?: string;
  /**
   * Google Maps Customer Identifier (CID) for the registered business — gives
   * pinpoint accuracy that beats any free-text address lookup. Decimal form.
   * Default is MTC's official Google Maps listing.
   */
  cid?: string;
  /** Optional shorter display label shown in the side card. */
  shortLabel?: string;
  /** Eyebrow line above the heading. */
  eyebrow?: string;
  /** Heading text. */
  heading?: string;
  /** Tone — "light" for cream-page backdrops (default), "dark" for dark sections. */
  tone?: 'light' | 'dark';
};

// MTC's verified Google Maps listing (CID 0x78393817edb7e8d in decimal).
const DEFAULT_CID = '541438564602969741';
const DEFAULT_ADDRESS = 'Moroccan Training Center, Casablanca, Maroc';

function embedSrc(cid: string, fallbackQuery: string): string {
  // The `cid=` parameter centres on the exact registered place; we keep `q=`
  // as a label so the iframe doesn't render a numeric ID in the popover.
  return `https://www.google.com/maps?cid=${encodeURIComponent(cid)}&output=embed&hl=fr&z=16&q=${encodeURIComponent(fallbackQuery)}`;
}

function openLink(cid: string): string {
  return `https://www.google.com/maps?cid=${encodeURIComponent(cid)}`;
}

export function MapSection({
  address = DEFAULT_ADDRESS,
  cid = DEFAULT_CID,
  shortLabel,
  eyebrow = 'Localisation',
  heading = 'Où nous trouver',
  tone = 'light',
}: Props) {
  const label = shortLabel ?? address;
  return (
    <section className={`mtc-map ${tone === 'dark' ? 'is-dark' : ''}`} aria-label="Localisation">
      <div className="mtc-map-inner">
        <div className="mtc-map-info">
          <span className="mtc-eyebrow">{eyebrow}</span>
          <h2 className="mtc-map-heading">{heading}</h2>
          <p className="mtc-map-address">{label}</p>
          <a
            className="mtc-map-link"
            href={openLink(cid)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ouvrir dans Google Maps <span aria-hidden="true">↗</span>
          </a>
          <div className="mtc-map-meta">
            <div>
              <span className="md-mono">Accueil</span>
              <strong>Lun – Ven · 9h–18h</strong>
            </div>
            <div>
              <span className="md-mono">Quartier</span>
              <strong>Maarif, Casablanca</strong>
            </div>
          </div>
        </div>
        <div className="mtc-map-frame">
          <iframe
            title="Carte du centre de formation MTC"
            src={embedSrc(cid, address)}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}
